/**
 * Imperative client→server actions for the cold-deck cache.
 *
 * Most sync traffic is automatic (store mutations → dirty-detect → push). The
 * two cold-deck messages are imperative side-channels:
 *   - `request-deck-cards` — pull a cold deck's cards when the user opens it.
 *   - `deck-activity-push`  — keep a deck warm on open (study/edit are already
 *     covered server-side when card pushes land).
 *
 * Both go through the live UserDo websocket via the provider's `sendMessage`
 * (the same channel the sync engine uses). No-ops when the socket isn't open.
 */

import { useCallback } from 'react';

import type { SyncClientMessage } from '../sync-engine/schema/sync-types';
import { awaitDeckRehydrated } from './internal/cold-deck-rehydrate';
import { diskFirstAllowed, loadDeckCardsFromDisk } from './internal/deck-disk-load';
import { deckCardsResident, deckStreamDeliveredNothing } from './internal/deck-residency';
import { useFlashkaStore } from './internal/store/flashka-store';
import { useUserDoConnection } from './provider/user-do-connection';

/** Minimal shape of the y-partyserver provider we rely on for custom sends. */
type SendableProvider = {
  ws?: { readyState: number } | null;
  sendMessage: (raw: string) => void;
};

const WS_OPEN = 1; // WebSocket.OPEN — literal so this stays RN/SSR-safe.

/**
 * What a caller acting on a PRESS passes as `connectWaitMs`: the provider
 * reconnects on its own, and an Add pressed during that blip otherwise fails
 * at once though the same press a moment later goes through. Not the default:
 * background sweeps and the backup export must fail fast on a closed socket,
 * or each cold deck stalls them this long.
 */
export const REHYDRATE_CONNECT_WAIT_MS = 5_000;
const CONNECT_POLL_MS = 100;

/** Resolves true once the provider's socket is open, false after `waitMs`. */
async function socketOpens(provider: SendableProvider, waitMs: number): Promise<boolean> {
  const deadline = Date.now() + waitMs;
  for (;;) {
    if (provider.ws?.readyState === WS_OPEN) return true;
    if (Date.now() >= deadline) return false;
    await new Promise((r) => setTimeout(r, CONNECT_POLL_MS));
  }
}

export type ColdDeckActions = {
  /** Ask the server to stream a (cold) deck's full card set. */
  requestDeckCards: (deckId: string) => void;
  /** Bump a deck's server-side activity so it stays warm. */
  bumpDeckActivity: (deckId: string, lastOpenedAtISO?: string) => void;
  /**
   * Make a deck's card bodies resident and wait until they are. Use this
   * when the caller must read the cards immediately after — the
   * share-to-group flow is the canonical example: it seeds StudyGroupDo from
   * `useFlashkaStore.cards[deckId]`, which is empty for a non-resident deck
   * until this resolves.
   *
   * Source, in order (`diskFirstAllowed`): resident already → nothing; this
   * device's storage, for a deck the server keeps warm (and for an archived
   * one only while the socket is closed — stale beats empty offline); then
   * the server (`request-deck-cards`, resolved by its `deck-cards-loaded`
   * completion signal). Rejects when the deck is still not resident and the
   * WS is not open (or has not opened within `connectWaitMs`; default 0), after `timeoutMs` (default 10s) without a reply, or when
   * the completed stream delivered NO cards for a deck whose meta says it has
   * some (`deckStreamDeliveredNothing`) — the server holds none, and a caller
   * that proceeded would share, copy or merge a hollow deck.
   * `skipDisk` is for a caller that has just run the disk step itself.
   */
  rehydrateColdDeck: (
    deckId: string,
    opts?: { timeoutMs?: number; skipDisk?: boolean; connectWaitMs?: number },
  ) => Promise<void>;
};

export function useColdDeckActions(): ColdDeckActions {
  const provider = useUserDoConnection() as unknown as SendableProvider | null;

  const send = useCallback(
    (msg: SyncClientMessage): void => {
      if (!provider || provider.ws?.readyState !== WS_OPEN) return;
      provider.sendMessage(JSON.stringify(msg));
    },
    [provider],
  );

  const requestDeckCards = useCallback(
    (deckId: string) => send({ type: 'request-deck-cards', deckId }),
    [send],
  );

  const bumpDeckActivity = useCallback(
    (deckId: string, lastOpenedAtISO: string = new Date().toISOString()) =>
      send({ type: 'deck-activity-push', deckId, lastOpenedAtISO }),
    [send],
  );

  const rehydrateColdDeck = useCallback(
    async (
      deckId: string,
      opts?: { timeoutMs?: number; skipDisk?: boolean; connectWaitMs?: number },
    ): Promise<void> => {
      // Already warm — nothing to do. A cheap fast-path only, and it must be
      // `deckCardsResident`, not `loadedDeckIds.has`: the loaded flag is
      // flipped optimistically alongside `requestDeckCards` and can be set
      // with no bodies resident (see deck-residency.ts) — returning early on
      // it left such a deck permanently empty.
      if (deckCardsResident(useFlashkaStore.getState(), deckId)) return;
      const online = !!provider && provider.ws?.readyState === WS_OPEN;
      // This device's rows first (see `diskFirstAllowed`): exact for a warm
      // deck, the only source offline, and no server round trip either way.
      if (
        !opts?.skipDisk &&
        diskFirstAllowed(useFlashkaStore.getState(), deckId, !online) &&
        (await loadDeckCardsFromDisk(deckId))
      ) {
        return;
      }
      if (!provider || !(await socketOpens(provider, opts?.connectWaitMs ?? 0))) {
        throw new Error('Cold-deck rehydrate requires an open connection');
      }
      // Register the waiter BEFORE sending so we don't race the reply
      // on fast local sockets / tests.
      const wait = awaitDeckRehydrated(deckId, opts?.timeoutMs ?? 10_000);
      provider.sendMessage(
        JSON.stringify({
          type: 'request-deck-cards',
          deckId,
        } satisfies SyncClientMessage),
      );
      await wait;
      // `deck-cards-loaded` fires even when the server streamed NOTHING (see
      // the UserDo handler): it marks the end of the reply, not that a body
      // arrived. Resolving here regardless let every "make it resident, then
      // read the cards" caller run on an empty bucket — the share-to-group
      // flow seeded the StudyGroupDo with 0 cards from a deck whose tile said
      // 189. Reject instead, like a timeout: the callers already treat a
      // rejected rehydrate as "could not load", never as "the deck is empty".
      if (deckStreamDeliveredNothing(useFlashkaStore.getState(), deckId)) {
        throw new Error(
          `Cold-deck rehydrate delivered no cards for ${deckId} (server holds none)`,
        );
      }
    },
    [provider],
  );

  return { requestDeckCards, bumpDeckActivity, rehydrateColdDeck };
}
